import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import find from 'lodash/find';
import unionBy from 'lodash/unionBy';
import uniqBy from 'lodash/uniqBy';
import qs from 'qs';
import csv from 'csv-stringify';

import endpoints from '../../../config/endpoints';

import intl from '../../shared/intl';
import http from '../../shared/http';
import createTable from '../components/table/general';
import createCalendar from '../../shared/calendar';
import createFilters from '../../shared/filters';
import {
	workingGeoFilter, utmTagFilters, dashboardVisitActionFilter, dashboardReferersFilter
} from '../../shared/filters/list';
import createGroups, { getGroupValues } from '../../shared/groups';
import getRequestQuery from '../shared/get-request-query';
import listOfFilters from '../shared/filters';
import checkPermissions from '../../shared/check-permissions';
import getFilterContext from '../shared/get-filters-context';
import { regions } from '../../../../../../enums/regions';

const isEverad = process.env.CPA_ENV === 'everad';
const isRocket = process.env.CPA_ENV === 'rocket';

const table = createTable({
	namespace: 'statistics',
	stateNodeName: 'table'
});

const calendar = createCalendar({
	namespace: 'statistics',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'statistics',
	stateNodeName: 'filters',
	queryBuildFunc: getRequestQuery,
	listOfFilters: [].concat(listOfFilters, [{
		name: 'session_ids',
		title: 'Split sessions',
		placeholder: 'Add split status',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.SPLIT_SESSIONS
		}
	}, {
		name: 'tags',
		title: 'Tags',
		placeholder: 'Add affiliate\'s tag',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATE_TAGS
		}
	}, {
		name: 'category',
		title: 'Category',
		placeholder: 'Add category',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: isEverad ? endpoints.LOOKUPS.OFFERS_CATEGORIES : endpoints.LOOKUPS.OFFERS_CATEGORIES_RP
		}
	}, {
		name: 'niche',
		title: 'Niches',
		placeholder: 'Add niche',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS_NICHES
		}
	}, {
		name: 'unacceptable_conversion_types',
		title: 'Auto trash types',
		placeholder: 'Enter auto trash types',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'static',
			source: 'root.lists.unacceptable-conversion-types'
		}
	}, {
		name: 'regions',
		title: 'Regions',
		placeholder: 'Add regions',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'provided',
			source: regions.map(reg => ({
				value: reg,
				label: reg
			}))
		}
	}, ...(!isEverad ? [workingGeoFilter, dashboardReferersFilter, dashboardVisitActionFilter, ...utmTagFilters] : [])])
});

const groups = createGroups({
	namespace: 'statistics',
	stateNodeName: 'groups'
});

// actions types
const FETCH_DATA_SUCCESS = 'statistics/fetch-data-success';
const FETCH_DATA_ERROR = 'statistics/fetch-data-error';
const FETCH_DATA_PROPOSE_DEFERRED = 'statistics/fetch-data-propose-deferred';
const APPLY = 'statistics/apply';
const SELECT_CURRENCY = 'statistics/select-currency';
const SHOW_PROGRESS_BAR = 'statistics/show-progress-bar';
const SET_DATA_USE_EXTERNAL_QUERY = `statistics/set-data-use-external-query`;

const SUBMIT_SUCCESS = 'statistics/submit-success';
const SUBMIT_ERROR = 'statistics/submit-error';

// permissions
export const allowedPermissions = {
	'view': 'analytics_view',
	'autotrash_view': 'autotrash_view'
};

// initial state
const initialState = {
	data: null,
	calendar: calendar.initialState,
	filters: filters.initialState,
	groups: groups.initialState,
	showProgressBar: false,
	table: table.initialState,
	query: {
		groups: ['date'],
		date_range: {
			start: null,
			end: null
		}
	},
	conversionsFrom: 1,
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: props => async (dispatch, getState) => {
		let externalQuery = await getExternalQuery(props.location, dispatch);

		if (!externalQuery) {
			return dispatch({
				type: FETCH_DATA_SUCCESS,
				payload: { data: [] }
			});
		}

		const savedFilters = filters.selectors.getSavedFilters(getState(), 'statistics');

		if (_get(savedFilters, 'entity.filters.length')) {
			if (isEmpty(externalQuery)) {
				externalQuery = savedFilters;
			} else {
				// merge savedFilters and externalQuery

				externalQuery = {
					entity: {
						...externalQuery.entity, // to get groups
						filters: uniqBy([...externalQuery.entity.filters, ...savedFilters.entity.filters], 'name')
					},
					query: {
						...externalQuery.entity, // to get groups
						filters: {
							...savedFilters.query.filters,
							...externalQuery.query.filters
						}
					}
				};
			}

			dispatch(replace(`statistics/general/?${qs.stringify(externalQuery)}`));
		}

		dispatch(actions.fetchRequest(externalQuery)).then(action => {
			if (externalQuery.entity) {
				const filtersList = externalQuery.entity.filters || [];
				dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));

				const { table, groups } = getState().statistics;

				const groupName = groups.listOfControls[0].name;

				const groupValue = (externalQuery.entity.groups || []).map((key) => {
					const group = table.groupColumns[key];
					return {
						value: group.index,
						label: group.title
					};
				});

				// update initial group state
				dispatch(actions.groupsSelectValue(groupName, groupValue));

				dispatch({
					type: SET_DATA_USE_EXTERNAL_QUERY,
					payload: externalQuery.entity
				});
			}
		});
	},

	requestDeferredStatistics: () => (dispatch, getState) => {
		const email = getState().root.data.email;
		confirm(
			`Your request failed to process in 30 seconds. Request deferred statistics to be sent to email ${email}?`
		) &&
			dispatch(actions.fetchRequest({}, true));
	},

	fetchRequest: (externalQuery = {}, deferred = false) => (dispatch, getState) => {
		const query = getRequestQuery(
			getState().statistics,
			externalQuery && externalQuery['query']
		);
		let url = `${endpoints.STATISTICS.GENERAL}?${query}`;
		if (deferred) {
			url += '&is_deferred=1';
		}
		return (http({
			url,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				deferred ? FETCH_DATA_ERROR : FETCH_DATA_PROPOSE_DEFERRED
			],
			addToResponse: {
				externalQuery,
				currentQuery: qs.parse(query)
			}
		}))(dispatch)
			.then(action => {
				if (action.type === FETCH_DATA_PROPOSE_DEFERRED) {
					dispatch(actions.requestDeferredStatistics());
				}
			});
	},

	apply: () => (dispatch, getState) => {
		dispatch(actions.showProgressBar());
		dispatch({
			type: APPLY,
			payload: getFormValues('conversionsFromStatisticsForm')(getState())
		});
		dispatch(actions.fetchRequest());
	},

	clear: () => dispatch => {
		dispatch(replace('/statistics'));
	},

	selectCurrency: id => dispatch => {
		dispatch({
			type: SELECT_CURRENCY,
			payload: id
		});

		dispatch(actions.apply());
	},

	showProgressBar: () => ({
		type: SHOW_PROGRESS_BAR
	}),

	statExport: () => (dispatch, getState) => {
		const { rows, groupsInfo, mapDowToDays } = selectors.getTable(getState());
		const currency = getState().statistics.query.currency;
		const translator = intl(getState());

		if (rows.length === 0) return;

		let headersConfig = [].concat(
			groupsInfo.map(entity => ({ id: entity.index, title: entity.title })),
			[{
				id: 'hosts',
				title: 'hosts'
			}, {
				id: 'hits',
				title: 'hits'
			}, {
				id: 'transitions',
				title: 'transitions'
			}, {
				id: 'europe_conversions',
				title: 'europe_conversions'
			}, {
				id: 'latin_conversions',
				title: 'latin_conversions'
			}, {
				id: 'asia_conversions',
				title: 'asia_conversions'
			}, {
				id: 'africa_conversions',
				title: 'africa_conversions'
			}, {
				id: 'accepted_conversions',
				title: 'approved_conversions'
			}, {
				id: 'manual_conversions',
				title: 'manual_conversions'
			}, {
				id: 'pending_conversions',
				title: 'pending_conversions'
			}, {
				id: 'declined_conversions',
				title: 'rejected_conversions'
			}, {
				id: 'rejected_approved_conversions',
				title: 'rejected_approved_conversions'
			}, {
				id: 'invalid_conversions',
				title: 'invalid_conversions'
			}, {
				id: 'total_conversions',
				title: 'total_conversions'
			}, {
				id: 'epc',
				title: 'epc'
			}, {
				id: 'crs',
				title: 'crs'
			}, {
				id: 'crl',
				title: 'crl'
			}, {
				id: 'approved',
				title: 'approve'
			}, {
				id: 'redeemed',
				title: 'redeemed'
			}, {
				id: 'currency',
				title: 'currency'
			}, {
				id: 'accepted_income',
				title: 'approved_payout'
			}, {
				id: 'pending_income',
				title: 'pending_payout'
			}, {
				id: 'declined_income',
				title: 'rejected_payout'
			}, {
				id: 'accepted_income_rub',
				title: 'accepted_income_rub'
			}, {
				id: 'accepted_income_usd',
				title: 'accepted_income_usd'
			}]
		);

		const exclude = fields => {
			headersConfig = headersConfig.filter(entity => fields.indexOf(entity.id) === -1);
		};

		if (!isRocket) {
			exclude(['europe_conversions', 'latin_conversions', 'asia_conversions', 'africa_conversions']);
		}

		// select All currency
		if (!currency) {
			exclude(['epc', 'accepted_income', 'pending_income', 'declined_income', 'currency']);
		}

		if (!isRocket || currency) {
			exclude(['accepted_income_rub', 'accepted_income_usd']);
		}

		let flattenRows = [];
		// tableRows is MAP structure
		rows.forEach(value => {
			flattenRows = flattenRows.concat(value.rows);
		});

		const headers = headersConfig.map(entity => entity.title.toLowerCase());

		const columns = flattenRows.filter(entity => !entity.isTotal).map(entity => {
			return headersConfig.map(header => {
				switch (header.id) {
					case 'hour': return !(isUndefined(entity[header.id]) || isNull(entity[header.id])) ?
						`${entity[header.id]}:00 - ${++entity[header.id]}:00` : '-';
					case 'dow': return translator(mapDowToDays[entity.dow]);
					case 'currency': return currency;
					default: return entity[header.id];
				}
			});
		});

		const rowsForExport = [].concat([headers], columns);

		csv(rowsForExport, (err, res) => {
			const exportFilename = `Export--Statistics--${moment().format(
				'YYYY-MM-DD--HH-mm-ss'
			)}.csv`;
			const csvData = new Blob([res], { type: 'text/csv;charset=utf-8;' });
			//IE11 & Edge
			if (navigator.msSaveBlob) {
				navigator.msSaveBlob(csvData, exportFilename);
			} else {
				//In FF link must be added to DOM to be clicked
				const link = document.createElement('a');
				link.href = window.URL.createObjectURL(csvData);
				link.setAttribute('download', exportFilename);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		});

	}
},
calendar.actions,
filters.actions,
groups.actions,
table.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: (state, action) => {
		if (action.payload.pathname.indexOf('statistics/general') !== -1) {
			if (!!action.payload.query && !isEmpty(action.payload.query)) {
				return {
					...state,
					filters: initialState.filters,
					data: null
				};
			}
		}

		if (action.payload.pathname === '/statistics' || action.payload.pathname.indexOf('statistics') === -1) {
			return {
				...initialState,
				query: {
					...initialState.query,
					currency: state.query.currency
				}
			};
		}

		return { ...state };
	},
	[FETCH_DATA_SUCCESS]: (state, action) => {
		const { externalQuery, currentQuery } = action.meta || {};

		let data = action.payload.data;

		const affiliatesFromExternalQuery = getInitialAffiliateListFromExternalQuery(externalQuery);
		const affiliatesFromFilter = getInitialAffiliateListFromFilter(state);

		// the following code displays all the affiliates from the filter
		// when grouping by affiliate
		if (affiliatesFromExternalQuery.length) {
			data = unionBy(data, affiliatesFromExternalQuery, 'affiliate');
		} else if (affiliatesFromFilter.length) {
			const secondGroup = state.groups.listOfControls[1].value[0];
			const secondGroupValue = secondGroup && secondGroup.value;

			data = unionBy(
				data, affiliatesFromFilter, item => `${item.affiliate}-${item[secondGroupValue] || '' }`
			);
		}

		if (state['conversionsFrom']) {
			const lessThen = entity => parseInt(entity['total_conversions']) >= parseInt(state['conversionsFrom']);
			data = data.filter(lessThen);
		}

		return {
			...state,
			data,
			showProgressBar: false,
			error: false,
			query: currentQuery ? currentQuery : state.query
		};
	},
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[FETCH_DATA_PROPOSE_DEFERRED]: state => ({ ...state, showProgressBar: false, data: [], error: false }),
	[APPLY]: (state, action) => ({
		...state,
		conversionsFrom: action.payload.conversionsFrom,
		query: {
			...state.query,
			groups: getGroupValues(
				state.groups.listOfControls,
				state.groups.orderControlsDuration
			)
		}
	}),
	[SELECT_CURRENCY]: (state, action) => ({
		...state,
		query: {
			...state.query,
			currency: action.payload
		}
	}),
	[SET_DATA_USE_EXTERNAL_QUERY]: (state, action) => {
		const { currency, date_range } = action.payload;

		if (currency || date_range) {
			const newQuery = currency ? { ...state.query, currency } : state.query;
			const newCalendar = date_range ? { ...state.calendar, value: date_range } : state.calendar;

			return {
				...state,
				query: newQuery,
				calendar: newCalendar
			};
		} else {
			return state;
		}
	},
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true})
},
calendar.reducers,
filters.reducers,
groups.reducers,
table.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.statistics.data,
	getTable: table.selectors.getData,
	getIntl: intl,
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.statistics.calendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.statistics.filters,
		state => state
	)(state),
	getListOfCurrencies: state => {
		const listOfCurrency = state.root.lists['available-currencies'];
		const all = { id: undefined, text: 'All' };
		return [all, ...listOfCurrency];
	},
	getCurrentCurrency: state => state.statistics.query.currency,
	getGroupsSelectControls: groups.selectors.getControls,
	getGroupsSelectOptions: groups.selectors.getOptions,
	getProgressBar: state => state.statistics.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getConversionsFilterInitialValues: state => ({
		conversionsFrom: state.statistics.conversionsFrom
	}),
	getFilterContext: state => getFilterContext(state.statistics, {
		date_range: {
			start: moment().format('YYYY-MM-DD'),
			end: moment().format('YYYY-MM-DD')
		}
	}),
	getError: state => state.statistics.error
};

async function getExternalQuery(location, dispatch) {
	const {
		mailings, type, popup, event, ...externalQuery
	} = (location && location.query && qs.parse(location.query)) || {};

	if (((mailings && type) || popup) && event) {
		let url = null;

		if (mailings) {
			url = `${endpoints.MAILING_EVENTS.AFFILIATES}?${qs.stringify({ mailings: mailings.split(','), type, event })}`;
		}

		if (popup) {
			url = `${endpoints.CUSTOM_POPUPS.AFFILIATES}?${qs.stringify({ popup_id: popup, event })}`;
		}

		const request = http({
			url,
			method: 'get',
			actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
		});

		const action = await request(dispatch);

		if (action.type === SUBMIT_SUCCESS) {
			const affiliates = action.payload.data;

			externalQuery.entity = {
				filters: [{
					name: 'affiliates',
					values: affiliates.map((affiliate) => ({
						value: affiliate.id,
						label: affiliate.email
					}))
				}],
				groups: ['affiliate']
			};

			externalQuery.query = {
				filters: {
					affiliates: affiliates.map((affiliate) => affiliate.id)
				},
				groups: ['affiliate']
			};
		}

		if (action.type === SUBMIT_ERROR) {
			return null;
		}
	}

	return externalQuery;
}

function createInitialAffiliateData({ label, value }) {
	return {
		affiliate: `${label}(${value})`,
		hosts: 0,
		hits: 0,
		transitions: 0,
		accepted_conversions: 0,
		pending_conversions: 0,
		declined_conversions: 0,
		total_conversions: 0,
		invalid_conversions: 0,
		redeemed_conversions: 0,
		accepted_crm_conversions: 0,
		accepted_income: 0,
		pending_income: 0,
		declined_income: 0,
		bonus: 0,
		manual_conversions: 0
	};
}

function getInitialAffiliateListFromExternalQuery(externalQuery) {
	if (externalQuery.entity && externalQuery.entity.groups && externalQuery.entity.filters) {
		if (externalQuery.entity.groups[0] === 'affiliate') {
			const affiliatesFilter = find(externalQuery.entity.filters, { name: 'affiliates' });

			if (affiliatesFilter && affiliatesFilter.values) {
				return affiliatesFilter.values.map(createInitialAffiliateData);
			}
		}
	}

	return [];
}

function getInitialAffiliateListFromFilter(state) {
	if (state.groups.listOfControls[0].value[0].value === 'affiliate') {
		const affiliatesFilter = find(state.filters.selectedFilters, { name: 'affiliates' });

		if (affiliatesFilter) {
			return affiliatesFilter.value.map(createInitialAffiliateData);
		}
	}

	return [];
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/statistics/general/index.js